// ─────────────────────────────────────────────────────────────────────────────
// TipsSettingsComponent
// ─────────────────────────────────────────────────────────────────────────────
// Settings → Product tours & tips, filed under **User** settings: tip state
// belongs to a person, not to a tenant, so nothing here affects colleagues.
//
// It is a render of the same registry the presenter fires from — no second list
// to keep in sync. That is also why the inventory doubles as the PM view of
// what is live, and why every tip is invocable manually (Preview), decoupled
// from its trigger: a tip dismissed too fast has to be replayable.
//
// Preferences apply on toggle and confirm with a toast — no Save button. The
// Orders settings page has one because it changes company behaviour; these are
// personal.
// ─────────────────────────────────────────────────────────────────────────────

import { Component, OnDestroy, OnInit } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

import { CommonService } from 'src/app/shared/common.service';
import { FtuePresenterService } from 'src/app/core/ftue/ftue-presenter.service';
import { FTUE_TIPS, ftueTipI18nKeys } from 'src/app/core/ftue/ftue-tip.registry';
import { IFtueTip } from 'src/app/core/ftue/ftue.models';

@Component({
  selector: 'app-tips-settings',
  templateUrl: './tips-settings.component.html',
  styleUrls: ['./tips-settings.component.scss'],
})
export class TipsSettingsComponent implements OnInit, OnDestroy {

  readonly tips = FTUE_TIPS;

  tipsEnabled = true;
  seenCount = 0;

  /** Exposed for the template — keys are derived from the tip id, not stored. */
  readonly i18nKeys = ftueTipI18nKeys;

  private readonly destroy$ = new Subject<void>();

  constructor(
    private readonly presenter: FtuePresenterService,
    private readonly translate: TranslateService,
    private readonly commonService: CommonService,
  ) {}

  ngOnInit(): void {
    this.presenter.state$
      .pipe(takeUntil(this.destroy$))
      .subscribe((state) => {
        this.tipsEnabled = state.tipsEnabled;
        this.seenCount = this.presenter.seenCount;
      });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  // ── Inventory ──────────────────────────────────────────────────────────────

  hasSeen(tip: IFtueTip): boolean {
    return this.presenter.hasSeen(tip);
  }

  /**
   * A retired announcement. Listed rather than hidden: the inventory doubles as
   * the record of what the product has announced, and Preview still replays it.
   */
  hasExpired(tip: IFtueTip): boolean {
    return this.presenter.hasExpired(tip);
  }

  statusKey(tip: IFtueTip): string {
    if (this.hasExpired(tip)) {
      return 'ftue.settings.expired';
    }
    return this.hasSeen(tip) ? 'ftue.settings.seen' : 'ftue.settings.not_seen';
  }

  statusTone(tip: IFtueTip): 'draft' | 'info' {
    return this.hasExpired(tip) || this.hasSeen(tip) ? 'draft' : 'info';
  }

  /** Replays a tip, ignoring seen state, budget and the master toggle. */
  preview(tip: IFtueTip): void {
    this.presenter.preview(tip.id);
  }

  // ── Preferences ────────────────────────────────────────────────────────────

  onTipsEnabledChange(enabled: boolean): void {
    this.presenter.setTipsEnabled(enabled);
    this.toast(enabled ? 'ftue.settings.tips_on_toast' : 'ftue.settings.tips_off_toast');
  }

  resetHistory(): void {
    this.presenter.resetHistory();
    this.toast('ftue.settings.history_reset_toast');
  }

  private toast(key: string): void {
    this.commonService.showToast(this.translate.instant(key), 'SUCCESS');
  }
}
